import { Button, Divider } from '@mui/material';

import type {
  FilterGroup,
  MultiGroupFilterServerSideSearch,
} from './MultiGroupFilter';

export interface MultiGroupFilterSelectAllProps {
  groupOptions?: FilterGroup[];
  selectedOptions?: FilterGroup[];
  onSelectedOptionsChange?: (selectedOptions: FilterGroup[]) => void;
  serverSideSearch?: MultiGroupFilterServerSideSearch;
  className: string;
}

/**
 * Selects or removes every group the filter can reach. It is rendered above
 * the options rather than among them, so it stays in place as the list is
 * scrolled and is still there when the list has nothing left to show.
 */
export const MultiGroupFilterSelectAll = ({
  groupOptions,
  selectedOptions,
  onSelectedOptionsChange,
  serverSideSearch,
  className,
}: MultiGroupFilterSelectAllProps) => {
  if (!groupOptions || !selectedOptions || !onSelectedOptionsChange) {
    return null;
  }

  const selectedOptionIds = new Set(selectedOptions.map((group) => group._id));

  const selectableOptions = groupOptions.filter(
    (group) => !group.disabledMessage,
  );

  const allQueriedOptionsSelected =
    Boolean(selectableOptions.length) &&
    selectableOptions.every((group) => selectedOptionIds.has(group._id));

  // A paginated filter only holds the loaded pages, so everything matching
  // the search is selected once the options are selected and exhausted.
  const allOptionsSelected = serverSideSearch
    ? allQueriedOptionsSelected && !serverSideSearch.hasNextPage
    : allQueriedOptionsSelected;

  const toggleSelectAll = () => {
    const groupOptionIds = new Set(groupOptions.map((group) => group._id));

    // Remove all removes every selected group whether it's in the search results or not
    if (allOptionsSelected) {
      onSelectedOptionsChange(
        selectedOptions.filter((group) => !groupOptionIds.has(group._id)),
      );
      return;
    }

    if (serverSideSearch) {
      serverSideSearch.selectAll.onSelectAll();
      return;
    }

    onSelectedOptionsChange([
      ...selectedOptions,
      ...selectableOptions.filter((group) => !selectedOptionIds.has(group._id)),
    ]);
  };

  return (
    <>
      <div
        className={className}
        // The listbox stops a mousedown blurring the input, and this sits
        // outside it. Without the same guard the input loses focus and the
        // popup closes before the click reaches the button.
        onMouseDown={(event) => event.preventDefault()}
      >
        <Button
          color="primary"
          onClick={toggleSelectAll}
          loading={serverSideSearch?.selectAll.loading ?? false}
        >
          {allOptionsSelected ? 'Remove all' : 'Select all'}
        </Button>
      </div>
      <Divider />
    </>
  );
};
